import { fasPhone } from 'shared/@generated/icons/fasPhone';
import { Platform, View } from 'react-native';
import { User, Breeder, Props, Pet } from 'shared/@types';
import { Image } from 'shared/components/image';
import { Text } from 'shared/components/text';
import { Icon } from 'shared/components/icon';
import type { MediaImageVersions } from 'shared/library/media-image-versions';

export type Avatarable = User | Breeder | Pet;

type AvatarMedia = {
  url?: string | null;
  thumbnail?: string | null;
  image_versions?: MediaImageVersions | [];
};

function resolveAvatarSize(className = '') {
  for (const token of className.split(/\s+/).filter(Boolean)) {
    if (token.includes(':')) {
      continue;
    }

    const match = token.match(/^size-(.+)$/);

    if (!match) {
      continue;
    }

    if (match[1] === 'px' || match[1] === 'full') {
      break;
    }

    const numericSize = Number(match[1]);

    if (!Number.isNaN(numericSize)) {
      // NativeWind's spacing unit is 3.5px (14px rem); the web uses 4px.
      return numericSize * (Platform.OS === 'web' ? 4 : 3.5);
    }
  }

  return 40;
}

const Avatar = ({
  model,
  containerClassName = '',
  imageClassName = '',
  ...props
}: Props<{
  model: Avatarable;
  containerClassName?: string;
  imageClassName?: string;
  className?: never;
}>) => {
  const initials = getInitials(model);
  const breederAvatar = 'breeder' in model ? model.breeder?.avatar : null;
  const avatar = model.avatar as AvatarMedia | null | undefined;
  const avatarUri = breederAvatar?.url ?? avatar?.url ?? avatar?.thumbnail ?? null;
  const imageVersions = breederAvatar?.url ? breederAvatar.image_versions : avatar?.image_versions;
  const avatarSize = resolveAvatarSize(containerClassName);
  const placeholderSize = avatarSize * 0.4;

  return (
    <View className={'relative'}>
      {avatarUri ? (
        <Image
          src={avatarUri}
          imageVersions={imageVersions}
          alt={initials}
          containerClassName={`${containerClassName}`}
          imageClassName={`border-2 border-white bg-white ${imageClassName}`}
          sizeType={'avatar'}
          width={avatarSize}
          sizes={`${avatarSize}px`}
          {...props}
        />
      ) : (
        <View
          className={`bg-brand-cream items-center justify-center overflow-hidden rounded-full ${containerClassName} ${imageClassName}`}
        >
          {initials.startsWith('+') ? (
            <Icon
              icon={fasPhone}
              className={'text-brand-brown-dark/70'}
              style={{ width: placeholderSize, height: placeholderSize }}
            />
          ) : (
            <Text
              className={'text-brand-brown-dark/70 font-bold'}
              style={{ fontSize: placeholderSize, lineHeight: placeholderSize * 1.25 }}
            >
              {initials}
            </Text>
          )}
        </View>
      )}
    </View>
  );
};

function getInitials(model: Avatarable): string {
  let name = '';

  if (model.hasOwnProperty('display_name')) {
    name = (model as User).display_name;
  } else {
    name = (model as Breeder).name;
  }

  if (!name) return '';

  return name
    .trim()
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export { Avatar };
